import { isLocalDev } from "@/helpers/browser";

export interface WaitForOptions {
  timeoutMs?: number;
  pollIntervalMs?: number;
  componentName?: string;
  errorMessage?: string;
  signal?: AbortSignal;
  /**
   * When true, suppress the verbose timeout `console.error` block on localhost
   * (replaced with a single `[dev]` console.info line). The Promise still
   * rejects with the same Error so caller flow is unchanged.
   */
  quietOnLocalhost?: boolean;
}

export default function waitFor(
  condition: () => boolean,
  options: WaitForOptions = {}
): Promise<void> {
  const {
    timeoutMs = 10000,
    pollIntervalMs = 100,
    componentName = "Component",
    errorMessage = "Condition not met",
    quietOnLocalhost = false,
  } = options;

  return new Promise((resolve, reject) => {
    const startTime = Date.now();
    let pollCount = 0;
    if (condition()) {
      resolve();
      return;
    }

    const intervalId = setInterval(() => {
      pollCount++;
      const elapsed = Date.now() - startTime;

      if (condition()) {
        clearInterval(intervalId);
        resolve();
        return;
      }

      if (elapsed >= timeoutMs) {
        clearInterval(intervalId);
        const error = new Error(
          `${errorMessage} within ${timeoutMs / 1000} seconds`
        );
        if (quietOnLocalhost && isLocalDev()) {
          console.info(
            `[dev] ${componentName} timed out after ${timeoutMs / 1000}s (expected on localhost)`
          );
        } else {
          console.error(
            `[${componentName}] ✗ Timeout after ${pollCount} attempts (${elapsed}ms)`,
            error
          );
        }
        reject(error);
      }
    }, pollIntervalMs);

    options.signal?.addEventListener("abort", () => {
      clearInterval(intervalId);
    });
  });
}
